import { RpcError } from '@protobuf-ts/runtime-rpc'
import { FeatureFlagIdOffboarding, FeatureFlagIdExportData } from 'c1gen/features'
import { TaskV1TaskServiceCreateOffboardingTask, UserV1UserServiceSetUserRoles, UserV1UserServiceUpdate } from 'c1gen/permissions'
import { C1MoreButton, C1MoreButtonOptionProps } from 'components/core/button'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import List from 'components/core/list'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1MultiUserDisplay } from 'components/core/multi-user/C1MultiUserDisplay'
import { VariableText } from 'components/core/variable-text'
import { useFeatureChecker, usePermissionChecker } from 'components/no-ui/capability'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { ChangeRoleRequestDialog } from 'components/pages/user/details/change-role-dialog'
import { UserFilters, UserFilterKeys } from 'components/pages/user/search'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserStatusDisplay } from 'components/pages/user/user_status'
import { useAlert } from 'hooks/useAlert'
import { UserValue } from 'lib/actions/user'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { useSimpleModal } from 'lib/modals'
import { useInitialFilters } from 'lib/search'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { TaskServiceCreateOffboardingRequest } from 'pbts/c1/api/task/v1/task'
import { SearchUsersRequest, UpdateUserRequest, User, UserOrigin, UserStatus } from 'pbts/c1/api/user/v1/user'
import { FieldMask } from 'pbts/google/protobuf/field_mask'
import { FC, useCallback, useLayoutEffect, useMemo, useState, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { createOffboardingTaskApi } from 'redux_toolkit/tasks'
import { updateUserApi, userSearchApi } from 'redux_toolkit/users'

// const tableStyles = {
//     userRow: { minWidth: '350px' },
// }

export const UserActions: FC<{ user: UserValue }> = ({ user: userView }) => {
    const permChecker = usePermissionChecker()
    const intl = useIntl()
    const toast = useAlert()
    const { open } = useSimpleModal()
    const { mutate } = updateUserApi()
    const { mutate: createOffboardingTask } = createOffboardingTaskApi()
    const hasOffboardingFeature = useFeatureChecker(FeatureFlagIdOffboarding)
    const userContext = useContext(UserContext)

    const canChangeRoles = permChecker(UserV1UserServiceSetUserRoles)
    const canChangeStatus = permChecker(UserV1UserServiceUpdate)

    const updateUser = useCallback(
        async (user: User): Promise<void> => {
            const status = user.status === UserStatus.DISABLED ? UserStatus.ENABLED : UserStatus.DISABLED
            mutate(
                UpdateUserRequest.create({
                    user: { ...user, status },
                    updateMask: FieldMask.create({ paths: ['status'] }),
                    expandMask: { paths: ['*'] },
                }),
            ).catch((error) => {
                toast(
                    intl.formatMessage(
                        {
                            id: 'error_user_set_status',
                            defaultMessage: 'Error changing user status. {code}: {msg}',
                        },
                        {
                            code: (error as RpcError).code,
                            msg: (error as RpcError).message,
                        },
                    ),
                    'error',
                )
            })
        },
        [intl, toast, mutate],
    )

    const onOffboard = useCallback(async (user: User): Promise<void> => {
        if (!user || !user.id) {
            return
        }
        const req = TaskServiceCreateOffboardingRequest.create({
            subjectUserId: user.id,
        })
        try {
            const data = await createOffboardingTask(req)
            if (data) {
                toast(
                    intl.formatMessage(
                        {
                            id: 'started_offboarding_user',
                            defaultMessage: 'Started offboarding {user}',
                        },
                        {
                            user: user.displayName || user.email || '',
                        },
                    ),
                    'success',
                )
            }
        } catch (err) {
            toast(
                intl.formatMessage(
                    {
                        id: 'error_offboarding_user',
                        defaultMessage: 'Unable to offboard user. {code}: {msg}',
                    },
                    {
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    },
                ),
                'error',
            )
        }
    }, [intl, toast, createOffboardingTask])

    const dropdownOptions: C1MoreButtonOptionProps[] = useMemo(() => {
        if (!userView) {
            return []
        }

        const statusChangeOption: C1MoreButtonOptionProps = {
            permission: UserV1UserServiceUpdate,
            key: 'components_user_details_delete_option',
            onClick: (): void => { updateUser(userView) },
            disabled: false,
            label: '',
        }

        if (userView.status === UserStatus.DISABLED) {
            statusChangeOption.label = intl.formatMessage({
                id: 'components_user_details_update_option_label_enable',
                defaultMessage: 'Activate',
            })
        } else if (userView.status === UserStatus.UNKNOWN || userView.status === UserStatus.ENABLED) {
            statusChangeOption.label = intl.formatMessage({
                id: 'components_user_details_update_option_label_disable',
                defaultMessage: 'Suspend',
            })
        } else {
            statusChangeOption.label = intl.formatMessage({
                id: 'components_user_details_update_option_label_disable',
                defaultMessage: 'Suspend',
            })
            statusChangeOption.disabled = true
        }

        return [
            ...(canChangeRoles ? [
                {
                    permission: UserV1UserServiceSetUserRoles,
                    key: 'components_user_details_update_user_role',
                    onClick: (): void => { open(ChangeRoleRequestDialog, { userId: userView.id, currentRoles: userView.roles || [] })() },
                    disabled: false,
                    label: intl.formatMessage({
                        id: 'components_user_details_update_option_user_role',
                        defaultMessage: 'Change role',
                    }),
                },
            ] : []),
            ...(canChangeStatus ? [
                statusChangeOption,
            ] : []),
            ...(hasOffboardingFeature && userContext.user.roleIds.includes(RoleID.SuperAdmin) ? [
                {
                    permission: TaskV1TaskServiceCreateOffboardingTask,
                    key: 'components_user_details_offboard_user',
                    onClick: (): void => { onOffboard(userView) },
                    disabled: false,
                    label: intl.formatMessage({
                        id: 'components_user_details_offboard_user',
                        defaultMessage: 'Start offboarding',
                    }),
                },
            ] : []),
        ] as C1MoreButtonOptionProps[]
    }, [intl, open, updateUser, userView, hasOffboardingFeature, userContext.user.roleIds, onOffboard, canChangeRoles, canChangeStatus])

    if (!dropdownOptions.length) {
        return null
    }

    return <C1MoreButton options={dropdownOptions} />
}

export enum UserListColumns {
    user = 'user',
    userReadOnly = 'userReadOnly',
    status = 'status',
    roles = 'roles',
    managers = 'managers',
    actions = 'actions',
    email = 'email',
    jobTitle = 'jobTitle',
    department = 'department',
    origin = 'origin',
}

export const userColsMap: Record<UserListColumns, C1TableReduxColumn<UserValue>> = {
    [UserListColumns.user]: {
        field: 'user',
        label: <FormattedMessage id="components_user_list_name" defaultMessage="Name" />,
        Component: ({ row }) => <UserDisplayName user={row} showTooltip />,
    },
    [UserListColumns.userReadOnly]: {
        field: 'user',
        label: <FormattedMessage id="components_user_list_name" defaultMessage="Name" />,
        Component: ({ row }) => <UserDisplayName user={row} showTooltip readOnly />,
    },
    [UserListColumns.status]: {
        field: 'status',
        label: <FormattedMessage id="components_user_list_status" defaultMessage="User status" />,
        Component: ({ row }) => <UserStatusDisplay status={row.status} />,
    },
    [UserListColumns.roles]: {
        field: 'roles',
        label: <FormattedMessage id="components_user_list_role" defaultMessage="Role" />,
        Component: ({ row }): React.ReactElement => {
            const roles = row.roles?.map((role) => role.displayName).join(', ') || ''
            return <VariableText variableText={roles} emptyState="Basic User" />
        },
    },
    [UserListColumns.managers]: {
        field: 'managers',
        label: <FormattedMessage id="components_user_list_manager" defaultMessage="Managers" />,
        Component: ({ row }): React.ReactElement => {
            return row.managers ? (
                <C1MultiUserDisplay users={row.managers} />
            ) : (
                <C1EmptyIndicator />
            )
        },
    },
    [UserListColumns.email]: {
        field: 'email',
        label: <FormattedMessage id="components_user_list_email" defaultMessage="Email" />,
        Component: ({ row }) => <VariableText variableText={row.email} />,
    },
    [UserListColumns.jobTitle]: {
        field: 'jobTitle',
        label: <FormattedMessage id="components_user_list_job_title" defaultMessage="Job title" />,
        Component: ({ row }) => <VariableText variableText={row.jobTitle} />,
    },
    [UserListColumns.department]: {
        field: 'department',
        label: <FormattedMessage id="components_user_list_department" defaultMessage="Department" />,
        Component: ({ row }) => <VariableText variableText={row.department} />,
    },
    [UserListColumns.origin]: {
        field: 'origin',
        label: <FormattedMessage id="components_user_list_origin" defaultMessage="Origin" />,
        Component: ({ row }): React.ReactElement => {
            const originLabels: Record<number, React.ReactElement> = {
                [UserOrigin.UNSPECIFIED]: <FormattedMessage id="user_origin_directory" defaultMessage="Directory" />,
                [UserOrigin.DIRECTORY]: <FormattedMessage id="user_origin_directory" defaultMessage="Directory" />,
                [UserOrigin.LOCAL]: <FormattedMessage id="user_origin_local" defaultMessage="Local" />,
                [UserOrigin.SYSTEM]: <FormattedMessage id="user_origin_system" defaultMessage="System" />,
            }
            return originLabels[row.origin] ?? <FormattedMessage id="user_origin_unknown" defaultMessage="Unknown" />
        },
    },
    [UserListColumns.actions]: {
        field: 'actions',
        isSticky: () => true,
        Component: ({ row }) => <UserActions user={row} />,
    },
}

/**
 * @designSystem
 */
export type C1UsersListProps = {
    columns?: UserListColumns[]
    filter?: Partial<SearchUsersRequest>
    displayFilters?: UserFilterKeys[]
    onRowClick?: (event: React.MouseEvent | React.KeyboardEvent, user: UserValue) => void
    selections?: UserValue[]
    disabledSelections?: UserValue[]
    setSelections?: (users: UserValue[]) => void
    emptyState?: React.ReactNode
    allowExport?: boolean
}

/**
 * Paginated user list with search, configurable columns, and gRPC data fetching.
 *
 * @designSystem
 * @category core
 * @relatedComponents DisplayNameDetails, C1Chip
 */
export const C1UsersList: FC<C1UsersListProps> = ({
    columns,
    filter: _filter,
    displayFilters,
    onRowClick,
    selections,
    disabledSelections,
    setSelections,
    emptyState,
    allowExport,
}) => {
    const intl = useIntl()
    const [page, setPage] = useState<number>(0)
    const hasExportDataFeature = useFeatureChecker(FeatureFlagIdExportData)

    // Get initial filters
    const initialFilter = useInitialFilters(SearchUsersRequest, ['userStatuses'])

    const [filter, setFilter] = useState<SearchUsersRequest>({
        ...initialFilter,
        ..._filter,
        expandMask: { paths: ['*'] },
    })

    // Sync state with the prop
    useLayoutEffect(() => {
        setFilter(() => ({ ...initialFilter, ..._filter, expandMask: { paths: ['*'] } }))
    }, [_filter, initialFilter])

    const combinedUserSearchRequestFilters = useMemo(() => {
        return {
            ...initialFilter,
            ...filter,
            expandMask: { paths: ['*'] },
        }
    }, [initialFilter, filter])

    const combinedUserSearchRequestFiltersDeps = JSON.stringify(combinedUserSearchRequestFilters)

    // NOTE: avoid render loops
    const userSearchRequest = useMemo(
        () => SearchUsersRequest.create(combinedUserSearchRequestFilters),
        [combinedUserSearchRequestFiltersDeps], // eslint-disable-line react-hooks/exhaustive-deps
    )

    const { data, isError, isLoading, getPage, canLoadMore, error, shouldInvalidateList } = userSearchApi.useSearch(
        userSearchRequest,
        { notify: true },
    )
    const invalidateUserList = userSearchApi.useInvalidateSearch()

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const userCols: Array<C1TableReduxColumn<UserValue>> = useMemo(() => {
        const columnsToUse = columns || [UserListColumns.user, UserListColumns.status, UserListColumns.roles, UserListColumns.managers]

        return columnsToUse.map((col) => userColsMap[col])
    }, [columns])

    const getRowAriaLabel = useCallback((user: UserValue): string => {
        return intl.formatMessage(
            { id: 'user_list_row_aria_label', defaultMessage: 'User {name}' },
            { name: user.displayName || user.email || '' },
        )
    }, [intl])

    const table = useMemo(
        () => (
            <C1TableRedux<UserValue>
                data={data}
                cols={userCols}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                onPageChange={onPageChange}
                error={error}
                shouldReload={shouldInvalidateList}
                onReload={invalidateUserList}
                page={page}
                onRowClick={onRowClick}
                selections={selections}
                disabledSelections={disabledSelections}
                setSelections={setSelections}
                emptyState={emptyState}
                getRowAriaLabel={getRowAriaLabel}
            />
        ),
        [canLoadMore, data, error, invalidateUserList, isError, isLoading,
            onPageChange, page, shouldInvalidateList, userCols, onRowClick,
            selections, disabledSelections, setSelections, emptyState, getRowAriaLabel],
    )

    const search = useMemo(() => {
        if (displayFilters && displayFilters.length > 0) {
            return (
                <UserFilters
                    onChange={(filterUpdate): void => {
                        setFilter((old) => {
                            const updated = SearchUsersRequest.create(old)
                            SearchUsersRequest.mergePartial(updated, filterUpdate)
                            if (SearchUsersRequest.equals(updated, old)) {
                                return old
                            }
                            return updated
                        })
                        setPage(0)
                    }}
                    initialState={userSearchRequest}
                    displayFilters={displayFilters}
                />
            )
        }
        return undefined
    }, [displayFilters, userSearchRequest])

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.USER_SEARCH,
            request: {
                oneofKind: 'searchUsersRequest',
                searchUsersRequest: userSearchRequest,
            },
        })
    }, [userSearchRequest])

    const buttons = useMemo(() => {
        if (allowExport && hasExportDataFeature) {
            return <ExportData exportDataRequest={exportDataActionRequest} />
        }
        return undefined
    }, [allowExport, hasExportDataFeature, exportDataActionRequest])

    return (
        <List<UserValue>
            table={table}
            search={search}
            buttons={buttons}
        />
    )
}
